import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'

const BURGUNDY = '#8B1A1A'
const GOLD = '#B8860B'
const CARD_BG = '#111111'
const BORDER = '#1e1e1e'
const TEXT_DIM = '#999'
const TEXT_MID = '#999'

const ROTATION_INTERVAL = 3500

const SPECIES_COLORS = [
  '#8B1A1A', '#B8860B', '#c0392b', '#d4a017', '#a93226',
  '#e67e22', '#922b21', '#b7950b', '#cb4335', '#d4ac0d',
  '#96281b', '#c9ae0a', '#b03a2e', '#e59866', '#a04000',
]

function useRotation(species, paused) {
  const [activeIdx, setActiveIdx] = useState(0)
  const timerRef = useRef(null)

  const advance = useCallback(() => {
    setActiveIdx(i => (i + 1) % species.length)
  }, [species.length])

  useEffect(() => { setActiveIdx(0) }, [species.length])

  useEffect(() => {
    if (paused || species.length <= 1) return
    timerRef.current = setInterval(advance, ROTATION_INTERVAL)
    return () => clearInterval(timerRef.current)
  }, [paused, advance, species.length])

  return [activeIdx, setActiveIdx]
}

const fmtY = v => {
  if (v >= 1_000_000_000) return (v / 1_000_000_000).toFixed(1) + 'B'
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + 'M'
  if (v >= 1_000) return (v / 1_000).toFixed(0) + 'K'
  return v.toLocaleString()
}

const DarkTooltip = ({ active, payload, label, unit }) => {
  if (!active || !payload?.length) return null
  return (
    <div style={{
      background: '#1a1a1a',
      border: `1px solid ${BORDER}`,
      borderRadius: '4px',
      padding: '0.6rem 0.9rem',
      fontSize: '0.78rem',
      color: '#ccc',
    }}>
      <div style={{ color: GOLD, fontWeight: 700, marginBottom: '0.3rem' }}>{label}</div>
      {payload.map((e, i) => (
        <div key={i} style={{ color: e.color }}>
          {e.name}: {Number(e.value).toLocaleString()} {unit}
        </div>
      ))}
    </div>
  )
}

function SpeciesChart({ title, subtitle, chartData, species, colorOffset, unit, gradId }) {
  const [paused, setPaused] = useState(false)
  const [manualIdx, setManualIdx] = useState(null)
  const [activeIdx, setActiveIdx] = useRotation(species, paused || manualIdx !== null)

  const currentIdx = manualIdx !== null ? manualIdx : activeIdx
  const activeSp = species[currentIdx]
  const color = SPECIES_COLORS[(currentIdx + colorOffset) % SPECIES_COLORS.length]
  const isRotating = manualIdx === null && !paused

  const handleClick = idx => {
    if (manualIdx === idx) {
      setManualIdx(null)
      setPaused(false)
      setActiveIdx(idx)
    } else {
      setManualIdx(idx)
    }
  }

  const handlePlayPause = () => {
    if (manualIdx !== null) {
      setActiveIdx(manualIdx)
      setManualIdx(null)
    }
    setPaused(p => !p)
  }

  return (
    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '6px', padding: '1.4rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.18em', color: GOLD, fontWeight: 600 }}>
            {title}
          </div>
          {subtitle && <div style={{ fontSize: '0.72rem', color: TEXT_DIM, marginTop: '0.2rem' }}>{subtitle}</div>}
        </div>
        <button
          onClick={handlePlayPause}
          style={{
            background: 'none',
            border: `1px solid ${isRotating ? BURGUNDY : '#444'}`,
            borderRadius: '20px',
            color: isRotating ? BURGUNDY : '#bbb',
            fontSize: '0.72rem',
            padding: '0.3rem 0.7rem',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            flexShrink: 0,
          }}
        >
          {isRotating ? (
            <>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
                <rect x="1" y="1" width="4" height="10" rx="1"/>
                <rect x="7" y="1" width="4" height="10" rx="1"/>
              </svg>
              Pause
            </>
          ) : (
            <>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
                <polygon points="1,1 11,6 1,11"/>
              </svg>
              {manualIdx !== null ? 'Auto' : 'Play'}
            </>
          )}
        </button>
      </div>

      {/* Species pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '1rem' }}>
        {species.map((sp, idx) => {
          const isActive = currentIdx === idx
          const bg = SPECIES_COLORS[(idx + colorOffset) % SPECIES_COLORS.length]
          return (
            <button
              key={sp}
              onClick={() => handleClick(idx)}
              style={{
                background: isActive ? bg : 'transparent',
                color: isActive ? '#fff' : bg,
                border: `1px solid ${bg}`,
                borderRadius: '20px',
                fontSize: '0.68rem',
                padding: '0.2rem 0.6rem',
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.55,
                fontWeight: isActive ? 600 : 400,
                transition: 'all 0.15s',
              }}
            >
              {sp}
            </button>
          )
        })}
      </div>

      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 5, right: 16, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={color} stopOpacity={0.35} />
              <stop offset="95%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e1e1e" />
          <XAxis dataKey="year" stroke="#333" style={{ fontSize: '10px' }} tick={{ fill: '#555' }} />
          <YAxis
            stroke="#333"
            style={{ fontSize: '10px' }}
            tick={{ fill: '#555' }}
            tickFormatter={fmtY}
            width={52}
          />
          <Tooltip content={<DarkTooltip unit={unit} />} />
          <Area
            type="monotone"
            dataKey={activeSp}
            stroke={color}
            fill={`url(#${gradId})`}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, fill: color }}
            isAnimationActive={true}
            animationDuration={500}
          />
        </AreaChart>
      </ResponsiveContainer>

      <div style={{ fontSize: '0.7rem', color: '#bbb', marginTop: '0.6rem' }}>
        Showing:{' '}
        <span style={{ color: TEXT_MID, fontWeight: 600 }}>{activeSp}</span>
        {isRotating && <span style={{ color: '#bbb' }}> — auto-rotating</span>}
      </div>
    </div>
  )
}

function TopSpeciesTable({ data, species }) {
  if (!data.length || !species.length) return null

  const latestYear = data[data.length - 1]?.year
  const latestRow = data[data.length - 1] || {}

  const ranked = species
    .map(sp => ({ species: sp, value: latestRow[sp] || 0 }))
    .filter(r => r.value > 0)
    .sort((a, b) => b.value - a.value)
    .slice(0, 10)

  if (!ranked.length) return null

  return (
    <div style={{ background: CARD_BG, border: `1px solid ${BORDER}`, borderRadius: '6px', padding: '1.4rem' }}>
      <div style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.18em', color: GOLD, fontWeight: 600, marginBottom: '0.8rem' }}>
        Top 10 Species by Landings — {latestYear}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #4a9eff' }}>
            <th style={{ textAlign: 'left', color: '#4a9eff', fontWeight: 500, padding: '0.4rem 0.6rem 0.4rem 0', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>#</th>
            <th style={{ textAlign: 'left', color: '#4a9eff', fontWeight: 500, padding: '0.4rem 0.6rem', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Species</th>
            <th style={{ textAlign: 'right', color: '#4a9eff', fontWeight: 500, padding: '0.4rem 0 0.4rem 0.6rem', fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Landings (lbs)</th>
          </tr>
        </thead>
        <tbody>
          {ranked.map((r, i) => (
            <tr
              key={r.species}
              style={{
                borderBottom: `1px solid #161616`,
                background: i % 2 === 0 ? 'transparent' : '#0d0d0d',
              }}
            >
              <td style={{ padding: '0.45rem 0.6rem 0.45rem 0', color: '#bbb', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</td>
              <td style={{ padding: '0.45rem 0.6rem', color: '#ccc' }}>{r.species}</td>
              <td style={{ padding: '0.45rem 0 0.45rem 0.6rem', textAlign: 'right', color: TEXT_MID, fontVariantNumeric: 'tabular-nums' }}>
                {r.value.toLocaleString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function DataCharts() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    setLoading(true)
    fetch('/api/data/forage', { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.json()
      })
      .then(d => { setData(d); setLoading(false) })
      .catch(e => { setError(e.message); setLoading(false) })
  }, [])

  const forageSpecies = useMemo(() => {
    if (!data) return []
    const s = new Set()
    data.forage_biomass.forEach(d => s.add(d.species))
    return Array.from(s).sort()
  }, [data])

  const forageBiomassChartData = useMemo(() => {
    if (!data) return []
    const byYear = {}
    data.forage_biomass.forEach(({ species, year, biomass_lbs }) => {
      const y = Math.floor(year)
      if (!byYear[y]) byYear[y] = { year: y }
      if (biomass_lbs) byYear[y][species] = (byYear[y][species] || 0) + biomass_lbs
    })
    return Object.values(byYear).sort((a, b) => a.year - b.year)
  }, [data])

  const fishingSpecies = useMemo(() => {
    if (!data) return []
    const s = new Set()
    data.forage_fishing.forEach(d => {
      if (typeof d.species === 'string' && isNaN(d.species) && d.species !== 'Year') s.add(d.species)
    })
    return Array.from(s).sort()
  }, [data])

  const fishingChartData = useMemo(() => {
    if (!data) return []
    const byYear = {}
    data.forage_fishing.forEach(({ species, year, landings_lbs }) => {
      if (typeof species !== 'string' || !isNaN(species) || species === 'Year') return
      const y = Math.floor(year)
      if (!byYear[y]) byYear[y] = { year: y }
      if (landings_lbs) byYear[y][species] = (byYear[y][species] || 0) + landings_lbs
    })
    return Object.values(byYear).sort((a, b) => a.year - b.year)
  }, [data])

  if (loading) {
    return (
      <div style={{ color: '#bbb', fontSize: '0.85rem', padding: '3rem 0', textAlign: 'center', fontStyle: 'italic' }}>
        Loading data...
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ color: BURGUNDY, fontSize: '0.85rem', padding: '3rem 0', textAlign: 'center' }}>
        Failed to load data: {error}
      </div>
    )
  }

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", animation: 'fadeIn 0.4s ease forwards' }}>
      <div style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.2em', color: GOLD, fontWeight: 600, marginBottom: '1.2rem' }}>
        Forage Fish Data & Charts
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        <SpeciesChart
          title="Forage Fish Biomass (lbs)"
          subtitle="Estimated population weight — NOAA StockSmart"
          chartData={forageBiomassChartData}
          species={forageSpecies}
          colorOffset={0}
          unit="lbs"
          gradId="grad-biomass"
        />
        <SpeciesChart
          title="Commercial Landings (lbs)"
          subtitle="Annual commercial landings — NOAA FOSS ODS, ACCSP"
          chartData={fishingChartData}
          species={fishingSpecies}
          colorOffset={5}
          unit="lbs"
          gradId="grad-landings"
        />
      </div>

      <TopSpeciesTable data={fishingChartData} species={fishingSpecies} />

      <div style={{ fontSize: '0.7rem', color: '#bbb', marginTop: '1rem', lineHeight: 1.6 }}>
        Data sources:{' '}
        <a
          href="https://apps-st.fisheries.noaa.gov/stocksmart"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#bbb', textDecoration: 'underline' }}
        >
          NOAA StockSmart
        </a>
        {' '}·{' '}
        <a
          href="https://www.fisheries.noaa.gov/foss"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#bbb', textDecoration: 'underline' }}
        >
          NOAA FOSS ODS
        </a>
        {' '}·{' '}
        <a
          href="https://www.accsp.org"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#bbb', textDecoration: 'underline' }}
        >
          ACCSP Data Warehouse
        </a>
        {data?.metadata?.imported_at && (
          <span> · Last updated: {new Date(data.metadata.imported_at).toLocaleDateString()}</span>
        )}
      </div>
    </div>
  )
}
